import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload, IconEdit } from '../components/icons';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

interface JournalEntryLine {
  id: number;
  chart_of_account: { code: string; name: string };
  debit_amount: string;
  credit_amount: string;
  description: string | null;
}

interface JournalEntryRecord {
  id: number;
  journal_number: string;
  entry_date: string;
  description: string;
  entry_type: string;
  status: string;
  total_debit: string;
  total_credit: string;
  created_by: { id: number; full_name: string } | null;
  posted_by: { id: number; full_name: string } | null;
  confirmed_by: { id: number; full_name: string } | null;
  lines: JournalEntryLine[];
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  posted: 'Posted',
  exported: 'Exported',
  confirmed: 'Confirmed',
};

export function JournalEntryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [entry, setEntry] = useState<JournalEntryRecord | null>(null);

  const canCreate = can('journals.create');
  const canPost = can('journals.post');
  const canExport = can('journals.export');
  const canConfirm = can('journals.confirm');
  const fmtMoney = (n: string) => formatMoney(Number(n), settings.currency);

  const load = () => api.get<JournalEntryRecord>(`/journal-entries/${id}`).then((res) => setEntry(res.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const post = async () => {
    if (!entry) return;
    if (!(await confirmAction('Post this journal entry? It becomes immutable once posted.', { confirmLabel: 'Post' }))) return;
    try {
      await api.post(`/journal-entries/${entry.id}/post`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Post failed.');
    }
  };

  const exportEntry = () => {
    if (!entry) return;
    downloadFile(api, `/journal-entries/${entry.id}/export`, `${entry.journal_number}.csv`).then(load);
  };

  const markConfirmed = async () => {
    if (!entry) return;
    if (!(await confirmAction('Confirm this journal entry has been keyed into FAMS?', { confirmLabel: 'Mark Confirmed' }))) return;
    try {
      await api.post(`/journal-entries/${entry.id}/mark-confirmed`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const reverse = async () => {
    if (!entry) return;
    if (!(await confirmAction('Create a reversing draft entry with every line\'s debit and credit swapped? It will still need to be posted separately.', { confirmLabel: 'Reverse' }))) return;
    try {
      const res = await api.post<JournalEntryRecord>(`/journal-entries/${entry.id}/reverse`);
      navigate(`/gl/journal-entries/${res.data.id}`);
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Reverse failed.');
    }
  };

  if (!entry) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/gl/journal-entries')} />
          <h1>{entry.journal_number}</h1>
        </div>
        <div className="page-header-actions">
          {entry.entry_type === 'contra' && <span className="badge badge-scheduled">Contra</span>}
          <span className="badge badge-neutral">{STATUS_LABELS[entry.status] ?? entry.status}</span>
          {canCreate && entry.status === 'draft' && (
            <IconButton icon={IconEdit} label="Edit" to={`/gl/journal-entries/${entry.id}/edit`} />
          )}
          {canPost && entry.status === 'draft' && (
            <button type="button" className="btn btn-primary" onClick={post}>
              Post
            </button>
          )}
          {canExport && entry.status === 'posted' && (
            <IconButton icon={IconDownload} label="Export CSV" onClick={exportEntry} />
          )}
          {canConfirm && ['posted', 'exported'].includes(entry.status) && (
            <button type="button" className="btn btn-primary" onClick={markConfirmed}>
              Mark Confirmed
            </button>
          )}
          {canCreate && entry.status !== 'draft' && (
            <button type="button" className="btn" onClick={reverse}>
              Reverse
            </button>
          )}
        </div>
      </header>

      <div className="card">
        <div className="detail-grid">
          <div>
            <h3>Entry Date</h3>
            <p>{entry.entry_date}</p>
          </div>
          <div>
            <h3>Description</h3>
            <p>{entry.description}</p>
          </div>
          <div>
            <h3>Drafted By</h3>
            <p>{entry.created_by?.full_name ?? '—'}</p>
          </div>
          <div>
            <h3>Posted By</h3>
            <p>{entry.posted_by?.full_name ?? '—'}</p>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Lines</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Account</th>
                <th>Description</th>
                <th>Debit</th>
                <th>Credit</th>
              </tr>
            </thead>
            <tbody>
              {entry.lines.map((line) => (
                <tr key={line.id}>
                  <td className="muted">
                    {line.chart_of_account.code} — {line.chart_of_account.name}
                  </td>
                  <td>{line.description ?? '—'}</td>
                  <td>{Number(line.debit_amount) > 0 ? fmtMoney(line.debit_amount) : ''}</td>
                  <td>{Number(line.credit_amount) > 0 ? fmtMoney(line.credit_amount) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} className="right">
                  <strong>Totals</strong>
                </td>
                <td>
                  <strong>{fmtMoney(entry.total_debit)}</strong>
                </td>
                <td>
                  <strong>{fmtMoney(entry.total_credit)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
}
